import { useEffect, useState } from "react";
import { api, type Cohort, type CohortMember, type FeatureFlag } from "../api/client";

// Phase 3a: read-only view of cohorts + feature flags.
// No mutate controls — those land in Phase 3c.

const REFRESH_MS = 30_000;
const TOP_MEMBERS_DEFAULT = 5;

export function Rollouts() {
  const [cohorts, setCohorts] = useState<Cohort[]>([]);
  const [flags, setFlags] = useState<FeatureFlag[]>([]);
  const [err, setErr] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [members, setMembers] = useState<Record<string, CohortMember[]>>({});

  const load = async () => {
    setLoading(true);
    try {
      const [{ cohorts: cs }, { flags: fs }] = await Promise.all([api.cohorts.list(), api.flags.list()]);
      setCohorts(cs);
      setFlags(fs);
      // Lazy-load top 5 members for each cohort in parallel.
      const memberResults = await Promise.all(
        cs.map((c) =>
          api.cohorts
            .get(c.name)
            .then((d) => [c.name, d.members] as const)
            .catch(() => [c.name, [] as CohortMember[]] as const),
        ),
      );
      const map: Record<string, CohortMember[]> = {};
      for (const [n, m] of memberResults) map[n] = m;
      setMembers(map);
      setErr(null);
    } catch (e) {
      setErr(String(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    const iv = setInterval(load, REFRESH_MS);
    return () => clearInterval(iv);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="space-y-6">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold">🚀 Канарейки и Флаги</h1>
          <p className="text-sm text-panel-muted">
            Cohorts ({cohorts.length}) · Feature flags ({flags.length}) · refresh каждые 30s
          </p>
        </div>
        <button className="btn text-xs" onClick={load} disabled={loading}>
          {loading ? "Загрузка…" : "Обновить"}
        </button>
      </header>

      {err && <div className="card pill-err border-panel-err">Ошибка: {err}</div>}

      {/* ------------------- Cohorts ------------------- */}
      <section className="space-y-2">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-panel-muted">Когорты</h2>
        <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
          {cohorts.map((c) => {
            const ms = members[c.name] ?? [];
            const isExpanded = !!expanded[c.name];
            const shown = isExpanded ? ms : ms.slice(0, TOP_MEMBERS_DEFAULT);
            return (
              <div key={c.name} className="card space-y-2">
                <div className="flex items-baseline justify-between">
                  <div className="text-base font-semibold capitalize">{c.name}</div>
                  <div className="text-xs text-panel-muted">
                    {c.member_count ?? ms.length}
                    {c.size_target != null && ` / ${c.size_target}`}
                  </div>
                </div>
                {c.description && (
                  <div className="text-xs text-panel-muted">{c.description}</div>
                )}
                {ms.length === 0 ? (
                  <div className="text-xs text-panel-muted italic">Нет участников</div>
                ) : (
                  <ul className="space-y-1 text-xs">
                    {shown.map((m) => (
                      <li key={m.company_id} className="truncate">
                        <span className="font-mono text-[10px] text-panel-muted">{m.company_id.slice(0, 8)}</span>{" "}
                        {m.company_name ?? <span className="text-panel-muted">(без имени)</span>}
                      </li>
                    ))}
                  </ul>
                )}
                {ms.length > TOP_MEMBERS_DEFAULT && (
                  <button
                    className="text-xs text-panel-muted hover:text-panel-fg"
                    onClick={() => setExpanded((e) => ({ ...e, [c.name]: !isExpanded }))}
                  >
                    {isExpanded ? "Свернуть ▴" : `Больше ▾ (+${ms.length - TOP_MEMBERS_DEFAULT})`}
                  </button>
                )}
              </div>
            );
          })}
          {cohorts.length === 0 && !loading && (
            <div className="card text-xs text-panel-muted md:col-span-3">
              Нет когорт. Миграция 003_canary_rollouts должна была их засеять — проверь backend.
            </div>
          )}
        </div>
      </section>

      {/* ------------------- Flags ------------------- */}
      <section className="space-y-2">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-panel-muted">Feature flags</h2>
        <div className="card overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead className="text-xs text-panel-muted">
              <tr>
                <th className="pb-2 pr-3">Ключ</th>
                <th className="pb-2 pr-3">Описание</th>
                <th className="pb-2 pr-3">Cohorts</th>
                <th className="pb-2 pr-3">%</th>
                <th className="pb-2 pr-3">Enabled</th>
                <th className="pb-2 pr-3">Disabled</th>
                <th className="pb-2">Default</th>
              </tr>
            </thead>
            <tbody>
              {flags.map((f) => (
                <tr key={f.key} className="border-t border-panel-border">
                  <td className="py-1.5 pr-3 font-mono text-xs">{f.key}</td>
                  <td className="py-1.5 pr-3 text-xs text-panel-muted">
                    {f.description ?? <span className="italic">—</span>}
                  </td>
                  <td className="py-1.5 pr-3 text-xs">
                    {f.enabled_cohorts.length === 0 ? (
                      <span className="text-panel-muted">—</span>
                    ) : (
                      f.enabled_cohorts.map((c) => (
                        <span key={c} className="pill mr-1">
                          {c}
                        </span>
                      ))
                    )}
                  </td>
                  <td className="py-1.5 pr-3 text-xs">{f.percent_rollout}</td>
                  <td className="py-1.5 pr-3 text-xs">{f.enabled_companies.length}</td>
                  <td className="py-1.5 pr-3 text-xs">{f.disabled_companies.length}</td>
                  <td className="py-1.5 text-xs">
                    <span className={`pill ${f.default_enabled ? "pill-ok" : ""}`}>
                      {f.default_enabled ? "on" : "off"}
                    </span>
                  </td>
                </tr>
              ))}
              {flags.length === 0 && (
                <tr>
                  <td colSpan={7} className="py-3 text-center text-xs text-panel-muted">
                    Флагов пока нет. Создай через <code>POST /api/flags</code>.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}
